import {
  Box,
  TouchableBox,
  Circle,
  AHText,
  AHIcon,
  AHTeamIconRelay,
  useUploadImage,
  Image,
  AHTextFonts,
} from "arkh";

import { graphql, useFragment, useMutation } from "react-relay";

import React, { useCallback, useMemo } from "react";

import {
  useOpenCamera,
  useOpenImagePicker,
} from "@/src/components/mobile/useImageActionSheet";
import { Platform } from "react-native";
import { CameraType } from "expo-image-picker";
import { DateTime } from "luxon";
import Color from "color";
import { ProfileScreenEmployee_employee$key } from "./__generated__/ProfileScreenEmployee_employee.graphql";
import { ProfileScreenEmployeeTodaysShifts_employee$key } from "./__generated__/ProfileScreenEmployeeTodaysShifts_employee.graphql";
import { ProfileScreenEmployeeTodaysShifts_employeeShift$key } from "./__generated__/ProfileScreenEmployeeTodaysShifts_employeeShift.graphql";
import { ProfileScreenEmployeeProfileImage_employee$key } from "./__generated__/ProfileScreenEmployeeProfileImage_employee.graphql";
import { ProfileScreenEmployeeSetProfileImageMutation } from "./__generated__/ProfileScreenEmployeeSetProfileImageMutation.graphql";
import { ProfileScreenEmployeeUploadLinkMutation } from "./__generated__/ProfileScreenEmployeeUploadLinkMutation.graphql";
import { ProfileScreenEmployeeEmployeeName_employee$key } from "./__generated__/ProfileScreenEmployeeEmployeeName_employee.graphql";

export function ProfileScreenEmployee(props: {
  employee: ProfileScreenEmployee_employee$key;
}) {
  const employee = useFragment(
    graphql`
      fragment ProfileScreenEmployee_employee on Employee {
        name
        ...ProfileScreenEmployeeProfileImage_employee
        ...ProfileScreenEmployeeEmployeeName_employee
        ...ProfileScreenEmployeeTodaysShifts_employee

      }
    `,
    props.employee
  );

  return (
    <>
      <Box spaceHorizontal="medium" spaceVertical="small">
        <ProfileImage employee={employee} />
      </Box>
      <Box spaceHorizontal="medium" spaceVertical="small">
        <EmployeeName employee={employee} />
      </Box>
      <Box spaceHorizontal="medium" spaceTop="medium" spaceBottom="small">
        <ProfileShifts employee={employee} />
      </Box>
    </>
  );
}

function ProfileShifts(props: {
  employee: ProfileScreenEmployeeTodaysShifts_employee$key;
}) {
  const employee = useFragment(
    graphql`
      fragment ProfileScreenEmployeeTodaysShifts_employee on Employee {
        id
        shifts {
          id
          ...ProfileScreenEmployeeTodaysShifts_employeeShift
        }
      }
    `,
    props.employee
  );

  if (employee.shifts.length === 0) {
    return null;
  }

  return (
    <Box>
      <AHText type="subtitle" color="text-secondary" spaceBottom="small">
        {DateTime.now().toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}
      </AHText>
      {employee.shifts.map((shift) => (
        <ProfileShift key={shift.id} shift={shift} />
      ))}
    </Box>
  );
}

function ProfileShift(props: {
  shift: ProfileScreenEmployeeTodaysShifts_employeeShift$key;
}) {
  const shift = useFragment(
    graphql`
      fragment ProfileScreenEmployeeTodaysShifts_employeeShift on EmployeeShift {
        id
        status
        formattedStartAtTime
        formattedEndAtTime
        secondaryScheduleType {
          color
          title
        }
        scheduleType {
          color
          title
          assignedTeams {
            id
            color
            ...AHTeamIcon
          }
        }
      }
    `,
    props.shift
  );

  const color = shift.scheduleType?.color ?? "#000";
  const isLightColor = useMemo(() => {
    return Color(color).darken(0.1).isLight();
  }, [color]);

  const isActive = shift.status === "ACTIVE";
  return (
    <Box
      style={{ flexDirection: "row", alignItems: "center" }}
      spaceBottom="small"
    >
      <Box
        style={{ width: 2, height: "100%" }}
        color={isActive ? "text-primary" : "text-secondary"}
        cornerRadius="medium"
      />
      <AHText
        spaceLeft="small"
        spaceRight="medium"
        color={isActive ? "text-primary" : "text-secondary"}
        style={{ minWidth: 105 }}
        type="subtitle"
      >
        {shift.formattedStartAtTime} - {shift.formattedEndAtTime}
      </AHText>
      <Box
        cornerRadius="small"
        style={{
          backgroundColor: shift.scheduleType?.color ?? "#FFF",
          flexDirection: "row",
          alignItems: "center",
          flexGrow: 0,
          flexShrink: 1,
        }}
        spaceVertical="xxsmall"
        spaceHorizontal="small"
      >
        <AHText
          style={{ flexGrow: 0, flexShrink: 1 }}
          color={isLightColor ? "text-primary" : "text-inverted"}
          type="info-small"
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {shift.scheduleType?.title}
        </AHText>
        {shift.scheduleType?.assignedTeams.map((team) => (
          <AHTeamIconRelay
            key={team.id}
            team={team}
            color={isLightColor ? "text-primary" : "text-inverted"}
            size="xsmall"
            spaceLeft="xsmall"
          />
        ))}
      </Box>
      {shift.secondaryScheduleType ? (
        <Box
          style={{
            flexDirection: "row",
            alignItems: "center",
            flexShrink: 1,
          }}
          spaceHorizontal="small"
        >
          <Circle
            size="dot"
            style={{
              backgroundColor: Color(
                shift.secondaryScheduleType.color ?? "#000"
              ).toString(),
            }}
          />
          <AHText
            spaceLeft="xsmall"
            type="detail"
            numberOfLines={1}
            ellipsizeMode="tail"
            color="darkerText"
          >
            {shift.secondaryScheduleType.title}
          </AHText>
        </Box>
      ) : null}
    </Box>
  );
}

function ProfileImage(props: {
  employee: ProfileScreenEmployeeProfileImage_employee$key;
}) {
  const employee = useFragment(
    graphql`
      fragment ProfileScreenEmployeeProfileImage_employee on Employee {
        id
        image {
          ... on Image {
            id
            ...Image_image
            __typename
          }
        }
      }
    `,
    props.employee
  );

  const [setProfileImage] =
    useMutation<ProfileScreenEmployeeSetProfileImageMutation>(graphql`
      mutation ProfileScreenEmployeeSetProfileImageMutation(
        $imageId: ID!
        $employeeId: ID!
      ) @raw_response_type {
        employee(id: $employeeId) {
          setProfileImage(imageId: $imageId) {
            id
            image {
              ... on Image {
                id
                __typename
              }
            }
          }
        }
      }
    `);

  const [generateUploadLink] =
    useMutation<ProfileScreenEmployeeUploadLinkMutation>(graphql`
      mutation ProfileScreenEmployeeUploadLinkMutation {
        uploadImageLink {
          image {
            id
          }
          url
        }
      }
    `);

  const { uploadImage, prepareForImageUpload } = useUploadImage();
  const onAddImage = useCallback(
    (uri: string, { width, height }: { width: number; height: number }) => {
      const uploadIdRequest = prepareForImageUpload({
        width,
        height,
        localImageURI: uri,
      });
      generateUploadLink({
        variables: {},
        onCompleted: (data) => {
          uploadIdRequest.then((uploadId) => {
            if (data.uploadImageLink) {
              uploadImage({
                imageId: data.uploadImageLink.image.id,
                uploadURL: data.uploadImageLink.url,
                uploadId,
              });
              setProfileImage({
                variables: {
                  imageId: data.uploadImageLink.image.id,
                  employeeId: employee.id,
                },
                optimisticResponse: {
                  employee: {
                    setProfileImage: {
                      id: employee.id,
                      image: {
                        __typename: "Image",
                        id: data.uploadImageLink.image.id,
                      },
                    },
                  },
                },
              });
            }
          });
        },
      });
    },
    [
      employee.id,
      generateUploadLink,
      prepareForImageUpload,
      setProfileImage,
      uploadImage,
    ]
  );

  const { openCamera, } = useOpenCamera({
    onAddImage: onAddImage,
    options: {
      allowsEditing: true,
      aspect: [1, 1],
    },
  });
  const { openImagePicker, } = useOpenImagePicker(
    {
      onAddImage: onAddImage,
      options: {
        allowsEditing: true,
        aspect: [1, 1],
        cameraType: CameraType.front,
      },
    }
  );

  return (
    <TouchableBox
      onPress={() => {
        if (Platform.OS === "web") {
          openImagePicker();
        } else {
          openCamera();
        }
      }}
    >
      <Circle
        size="xlarge"
        color="surface-backgroundControl"
        borderColor="border-primary"
        style={{
          borderWidth: 1,
          overflow: "hidden",
        }}
      >
        {employee.image?.__typename === "Image" ? (
          <Image
            image={employee.image}
            type="remote"
            style={{ width: "100%", height: "100%" }}
          />
        ) : (
          <AHIcon type="camera" color="text-secondary" />
        )}
      </Circle>
    </TouchableBox>
  );
}

function EmployeeName(props: {
  employee: ProfileScreenEmployeeEmployeeName_employee$key;
}) {
  const employee = useFragment(
    graphql`
      fragment ProfileScreenEmployeeEmployeeName_employee on Employee {
        name
        assignedRoles {
          id
          name
        }
      }
    `,
    props.employee
  );

  return <>
  <AHText type="title-big">{employee.name}</AHText>
    {employee.assignedRoles.length > 0 ? (
      <Box spaceTop="small" style={{ flexDirection: "row", alignItems: "center" }}>
        <AHIcon type="employee-role" size="medium" color="text-secondary" spaceRight="small" />
        <AHText type="body" font={AHTextFonts.MEDIUM} color="text-secondary">
          {employee.assignedRoles.map((role) => role.name).join(", ")}
        </AHText>
      </Box>
    ) : null}
    </>;
}
